import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { PackageOpen } from 'lucide-react';
import { useState } from 'react';
import { formatMoney } from '@nokowazzi/types';
import { Button, EmptyState, Input, Select, Spinner } from '@nokowazzi/ui';
import { Table, Td, Th, Thead, Tr } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import type { AdminOrderListItem, AdminOrderListResponse } from '../features/orders/queries';
import { ORDER_STATUS_FILTER_OPTIONS, OrderStatusBadge } from '../features/orders/status';
import { ApiError, apiFetch } from '../lib/api';
import { formatDateTime } from '../lib/datetime';

export const Route = createFileRoute('/_admin/orders')({
  component: OrdersScreen,
});

function OrdersScreen() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');

  const listQuery = useInfiniteQuery({
    queryKey: ['admin', 'orders', 'list', { q, status: statusFilter }] as const,
    queryFn: ({ pageParam }) =>
      apiFetch<AdminOrderListResponse>('/v1/admin/orders', {
        query: {
          q: q || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          cursor: pageParam ?? undefined,
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const items: AdminOrderListItem[] = listQuery.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader title="거래 관리" description="에스크로 주문 조회 및 수동 처리" />

      <div className="mb-4 flex items-center gap-2">
        <Select
          className="w-40"
          aria-label="상태 필터"
          options={ORDER_STATUS_FILTER_OPTIONS}
          value={statusFilter}
          onValueChange={setStatusFilter}
        />
        <form
          className="w-72"
          onSubmit={(event) => {
            event.preventDefault();
            setQ(searchInput.trim());
          }}
        >
          <Input
            type="search"
            placeholder="주문번호·요청·닉네임 검색 (Enter)"
            aria-label="거래 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            clearable
            clearLabel="검색어 지우기"
          />
        </form>
      </div>

      {listQuery.isPending ? (
        <div className="flex min-h-52 items-center justify-center">
          <Spinner label="거래 목록 불러오는 중" />
        </div>
      ) : listQuery.isError ? (
        <EmptyState
          title="거래 목록을 불러오지 못했습니다"
          description={
            listQuery.error instanceof ApiError ? listQuery.error.message : '잠시 후 다시 시도해 주세요'
          }
          action={
            <Button variant="tertiary" size="sm" onClick={() => void listQuery.refetch()}>
              다시 시도
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<PackageOpen size={40} strokeWidth={1.5} />}
          title="거래가 없습니다"
          description="조건에 맞는 주문이 없습니다"
        />
      ) : (
        <>
          <Table>
            <Thead>
              <tr>
                <Th className="w-28">주문번호</Th>
                <Th>요청</Th>
                <Th className="w-28">상태</Th>
                <Th>구매자</Th>
                <Th>판매자</Th>
                <Th className="w-32 text-right">금액</Th>
                <Th className="w-36">생성일</Th>
              </tr>
            </Thead>
            <tbody>
              {items.map((order) => (
                <Tr key={order.id} className="hover:bg-subtle">
                  <Td>
                    <Link
                      to="/orders/$id"
                      params={{ id: order.id }}
                      className="font-mono text-caption1 font-medium text-fg-brand hover:underline"
                    >
                      {order.id.slice(0, 8)}
                    </Link>
                  </Td>
                  <Td>
                    <span className="line-clamp-1 text-fg">{order.request?.title ?? '—'}</span>
                  </Td>
                  <Td>
                    <OrderStatusBadge status={order.status} />
                  </Td>
                  <Td className="text-fg-secondary">{order.buyer?.display_name ?? '—'}</Td>
                  <Td className="text-fg-secondary">{order.seller?.display_name ?? '—'}</Td>
                  <Td className="text-right tabular-nums">
                    {formatMoney({ amount: order.total_amount, currency: order.currency })}
                  </Td>
                  <Td className="whitespace-nowrap text-caption2 text-fg-tertiary">
                    {formatDateTime(order.created_at)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>

          {listQuery.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="tertiary"
                size="sm"
                loading={listQuery.isFetchingNextPage}
                onClick={() => void listQuery.fetchNextPage()}
              >
                더 보기
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
